<template>
  <Table
    ref="table"
    service="ActivityReport"
    :min-width="groupByPerson ? '1100px' : '2100px'"
    :initialize="initialize"
    :exportData="exportData"
    :params="params"
    :headers="headers"
    :messages="messages"
    :show-select="false"
    :show-filter-button="false"
    :no-edit="true"
    show-actions="export"
  >
    <template v-slot:submenu-middle>
      <h4 v-if="!groupByPerson" class="mr-10">Centros: {{ totals.places }}</h4>
      <h4 class="mr-10">Horas: {{ totals.hours }}</h4>
      <h4 class="mr-10">Visitas: {{ totals.visits }}</h4>
      <h4 class="mr-10">Importe estimado: {{ totals.amount }} €</h4>
      <h4 v-if="totals.mixedRates" class="mr-10 red--text">
        Líneas con tarifas dispares: {{ totals.mixedRates }}
      </h4>
      <v-switch
        v-model="groupByPerson"
        class="mt-0 mr-10 pt-0"
        label="Agrupar por persona"
        hide-details
        dense
      />
      <DChangeRangeDate
        v-model="range"
      />
    </template>
  </Table>
</template>

<script>
import Table from "@/components/core/Table.vue";
import moment from "moment";
import { formatDate } from "@/utils";
import { mapState } from 'vuex';

export default {
  components: { Table },
  data() {
    // Defaults to the last complete natural quarter, which is the period usually asked for.
    const end = moment().startOf('quarter').subtract(1, 'day').endOf('day');
    const start = end.clone().startOf('quarter');
    return {
      messages: { item: "Informe de actividad" },
      range: [ start.valueOf(), end.valueOf() ],
      groupByPerson: false
    };
  },
  computed: {
    ...mapState([ 'core' ]),
    params() {
      return {
        range: this.range,
        grouped: this.groupByPerson
      };
    },
    rows() {
      return this.core ? this.core.data || [] : [];
    },
    totals() {
      const rows = this.rows;

      // Only in the detail: there each row is one centre, so they can be counted without
      // repeating. Adding up the per person counts would count a shared centre once per
      // person, so the figure is left out of the grouped view instead of shown wrong.
      const places = this.groupByPerson
        ? 0
        : new Set( rows.map( row => row.idPlace ).filter( id => id != null )).size;

      const hours = rows.reduce(( total, row ) => total + ( row.hours || 0 ), 0 );
      const visits = rows.reduce(( total, row ) => total + ( row.visits || 0 ), 0 );
      const amount = rows.reduce(( total, row ) => total + ( row.amount || 0 ), 0 );

      // Lines whose days do not share a single rate. Should always be zero: it only happens if
      // the rate of a campaign place was edited instead of being superseded by a new row.
      // In the detail each row is a line; grouped, each row already carries how many of that
      // person's lines are affected, so the figure means the same in both views.
      const mixedRates = this.groupByPerson
        ? rows.reduce(( total, row ) => total + ( row.mixedRateLines || 0 ), 0 )
        : rows.filter( row => ( row.distinctRates || 1 ) > 1 ).length;

      return {
        places,
        visits,
        mixedRates,
        hours: this.decimal( hours ),
        amount: this.decimal( amount )
      };
    },
    headers() {

      if ( this.groupByPerson ) {
        return [
          { text: "PERSONA", value: "user", display: (a) => a.surname + ", " + a.name, filterable: false },
          { text: "CENTROS", value: "places", display: Number, filterable: false },
          { text: "CAMPAÑAS", value: "campaigns", display: Number, filterable: false },
          { text: "JORNADAS", value: "days", display: Number, filterable: false },
          { text: "JORNADAS VALIDADAS", value: "validatedDays", display: Number, filterable: false },
          { text: "HORAS VALIDADAS", value: "hours", display: Number, filterable: false },
          { text: "VISITAS VALIDADAS", value: "visits", display: Number, filterable: false },
          { text: "IMPORTE ESTIMADO", value: "amount", display: Number, filterable: false },
          // Same signal as the TARIFAS column of the detail, counted over this person's lines.
          { text: "TARIFAS DISPARES", value: "mixedRateLines",
            display: ( value ) => value > 0 ? "⚠ " + value : "", filterable: false }
        ];
      }

      return [
        { text: "CENTRO", value: "placeName", width: 250, filterable: false },
        { text: "CÓDIGO", value: "placeCode", filterable: false },
        { text: "POBLACIÓN", value: "city", filterable: false },
        { text: "PROVINCIA", value: "province", filterable: false },
        { text: "PERSONA", value: "user", width: 200, display: (a) => a.surname + ", " + a.name, filterable: false },
        { text: "CAMPAÑA", value: "campaignName", width: 250, filterable: false },
        { text: "CLIENTE", value: "clientName", filterable: false },
        { text: "MODALIDAD", value: "mode", filterable: false },
        { text: "PRECIO HORA", value: "costHour", display: Number, filterable: false },
        { text: "PRECIO VISITA", value: "costVisit", display: Number, filterable: false },
        { text: "PLUS JORNADA", value: "extraCostDay", display: Number, filterable: false },
        // Empty when the line has a single rate, which is the normal case. A number here means
        // the days of the line carry rates that do not agree, so the amount was accumulated
        // over several and the rate columns show only the last one.
        { text: "TARIFAS", value: "distinctRates",
          display: ( value ) => value > 1 ? "⚠ " + value : "", filterable: false },
        { text: "JORNADAS", value: "days", display: Number, filterable: false },
        { text: "JORNADAS VALIDADAS", value: "validatedDays", display: Number, filterable: false },
        { text: "HORAS VALIDADAS", value: "hours", display: Number, filterable: false },
        { text: "VISITAS VALIDADAS", value: "visits", display: Number, filterable: false },
        { text: "PRIMERA JORNADA", value: "firstDay", display: this.date, filterable: false },
        { text: "ÚLTIMA JORNADA", value: "lastDay", display: this.date, filterable: false },
        { text: "IMPORTE ESTIMADO", value: "amount", display: Number, filterable: false }
      ];
    }
  },
  watch: {
    $route() {
      this.$nextTick( this.setTitle );
    }
  },
  methods: {
    initialize( params ) {
      params.filters = ( params.filters || [] ).concat( this.reportFilters());

      return this.$store.dispatch( 'page', params );
    },
    exportData( params ) {
      params.filters = ( params.filters || [] ).concat( this.reportFilters());

      return this.$store.dispatch( 'downloadData', params );
    },
    date( value ) {
      if ( value ) {
        return formatDate( value, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit"
        });
      }
      return "";
    },
    setTitle() {
      const title = 'Informe de actividad';
      return this.$store.commit( 'setTitle', title );
    },
    reportFilters() {
      return [
        { field: 'date', rangeValue: {
          start: moment( this.range[0] ).startOf('day').valueOf(),
          end: moment( this.range[1] ).endOf('day').valueOf()
        }},
        { field: 'grouped', booleanValue: this.groupByPerson }
      ];
    },
    decimal( value ) {
      return ( Math.round( value * 100 ) / 100 )
        .toLocaleString( 'es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  },
  mounted() {
    this.setTitle();
  }
};
</script>
